import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Tag, Empty, message } from "antd";
import debounce from "lodash/debounce";
import { getAggregatorCatalogueProducts } from "../../services/index";
import { isItemOutOfStock } from "../../utils/aggregatorStockCheck";

const formatCurrency = (amount) => `₹${(Number(amount) || 0).toFixed(2)}`;

// TSK-3229: single-step out-of-stock item substitution for AK/FR aggregator
// orders. Two internal views mirror the solution doc's wireframes — "replace"
// (item list, inline catalogue search per OOS line) and "confirm" (change
// summary + financial impact) — both submitted in one posAggregatorItemModify
// call, no draft/approval step. Replacement candidates come from the store's
// OWN aggregator catalogue via getAggregatorCatalogueProducts, which already
// returns per-candidate stock — no separate stock round trip needed here.
const ModifyItemsModal = ({ visible, order, stockMap, tillId, onClose, onConfirm, confirming }) => {
  const [view, setView] = useState("replace");
  const [replacements, setReplacements] = useState({});
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const items = order?.items || [];

  useEffect(() => {
    if (!visible) {
      setView("replace");
      setReplacements({});
      setActiveIndex(null);
      setSearchValue("");
      setSearchResults([]);
    }
  }, [visible]);

  const runSearch = useMemo(
    () =>
      debounce(async (term) => {
        if (!term || term.trim().length < 2) {
          setSearchResults([]);
          setSearching(false);
          return;
        }
        try {
          const response = await getAggregatorCatalogueProducts(tillId, term.trim());
          const results = response?.data?.data?.getAggregatorCatalogueProducts || [];
          setSearchResults(results);
        } catch (error) {
          console.error("Error searching catalogue products:", error);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      }, 300),
    [tillId]
  );

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const handleReplaceClick = (index) => {
    setActiveIndex(index);
    setSearchValue("");
    setSearchResults([]);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setSearching(!!value && value.trim().length >= 2);
    runSearch(value);
  };

  const handleSelectResult = (index, product) => {
    setReplacements((prev) => ({ ...prev, [index]: product }));
    setActiveIndex(null);
  };

  const handleChangeReplacement = (index) => {
    setReplacements((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    handleReplaceClick(index);
  };

  const replacementCount = Object.keys(replacements).length;

  const handleSaveChanges = () => {
    if (replacementCount === 0) {
      message.error("Select at least one replacement before saving.");
      return;
    }
    setView("confirm");
  };

  const originalTotal = order?.amount || 0;
  const netDifference = Object.entries(replacements).reduce((sum, [index, product]) => {
    const item = items[index];
    return sum + ((Number(product.sellingPrice) || 0) - (Number(item.price) || 0)) * (item.quantity || 1);
  }, 0);
  const amendedTotal = originalTotal + netDifference;

  const handleConfirm = () => {
    const lineChanges = Object.entries(replacements).map(([index, product]) => ({
      sOrderlineId: items[index].sOrderlineID,
      newProductId: product.mProductId,
    }));
    onConfirm(lineChanges);
  };

  return (
    <Modal
      open={visible}
      title={view === "replace" ? `Modify Order Items — #${order?.orderNo || ""}` : "Confirm Order Modification"}
      onCancel={onClose}
      width={480}
      footer={
        view === "replace"
          ? [
              <Button key="cancel" onClick={onClose} disabled={confirming}>
                Cancel
              </Button>,
              <Button key="save" type="primary" style={{ background: "#2F3856", borderColor: "#2F3856", color: "#fff" }} onClick={handleSaveChanges}>
                Save Changes
              </Button>,
            ]
          : [
              <Button key="back" onClick={() => setView("replace")} disabled={confirming}>
                Back
              </Button>,
              <Button key="confirm" type="primary" style={{ background: "#008000", borderColor: "#008000" }} onClick={handleConfirm} loading={confirming}>
                Confirm &amp; Update Order
              </Button>,
            ]
      }
    >
      {view === "replace" ? (
        <>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
            Select items to replace. Substitutes are shown from the store's UrbanPiper catalogue only.
          </div>

          {items.map((item, index) => {
            const oos = isItemOutOfStock(item, stockMap);
            const replacement = replacements[index];
            const isActive = activeIndex === index;
            const stockEntry = stockMap?.[item.sOrderlineID];
            const availableQty = stockEntry?.sellableQty ?? stockEntry?.onhandQty;

            if (!oos) {
              return (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 6, fontSize: 12 }}
                >
                  <div>
                    <div style={{ fontWeight: 500, color: "#111" }}>
                      {item.name} <span style={{ color: "#6b7280", fontWeight: 400 }}>x{item.quantity}</span>
                    </div>
                    <div style={{ fontSize: 10, color: "#6b7280" }}>
                      {formatCurrency(item.price)} each{availableQty != null && <> · <span style={{ color: "#16A34A" }}>Stock: {availableQty}</span></>}
                    </div>
                  </div>
                  <Tag style={{ fontSize: 8, fontWeight: 700, background: "#DCFCE7", color: "#16A34A", border: "1px solid rgba(22,163,74,0.2)" }}>In Stock</Tag>
                </div>
              );
            }

            return (
              <div key={index} style={{ padding: "8px 10px", border: "1px solid #FECACA", background: "#FEF2F2", borderRadius: 6, marginBottom: 6, fontSize: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 500, color: replacement ? "#111" : "#DC2626", textDecoration: replacement || isActive ? "line-through" : "none" }}>
                      {item.name} <span style={{ color: "#6b7280", fontWeight: 400 }}>x{item.quantity}</span>
                    </div>
                    {!replacement && !isActive && (
                      <div style={{ fontSize: 10, color: "#6b7280" }}>
                        {formatCurrency(item.price)} each · <span style={{ color: "#DC2626" }}>Stock: {availableQty ?? 0}</span>
                      </div>
                    )}
                  </div>
                  {!replacement && !isActive && (
                    <Button size="small" onClick={() => handleReplaceClick(index)} style={{ background: "#2F3856", color: "#fff", fontSize: 10, height: 24, border: "none" }}>
                      Replace
                    </Button>
                  )}
                  {isActive && <Tag style={{ fontSize: 8, fontWeight: 700, background: "#FEE2E2", color: "#DC2626", border: "1px solid rgba(220,38,38,0.2)" }}>Out of Stock</Tag>}
                </div>

                {isActive && (
                  <div style={{ marginTop: 8 }}>
                    <input
                      autoFocus
                      value={searchValue}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search from UrbanPiper catalogue..."
                      style={{ width: "100%", height: 32, border: "1px solid #d1d5db", borderRadius: 6, padding: "0 10px", fontSize: 12, marginBottom: 6 }}
                    />
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
                      {searching && <div style={{ padding: 8, fontSize: 11, color: "#6b7280" }}>Searching...</div>}
                      {(() => {
                        // Exclude the item being replaced from its own substitute list — it's
                        // on the catalogue (that's why it matched the search) but offering it
                        // as a "replacement" for itself is meaningless.
                        const candidates = searchResults.filter((product) => product.mProductId !== item.productId);
                        if (searching) return null;
                        if (searchValue.trim().length >= 2 && candidates.length === 0) {
                          return <Empty description="No matches" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 8 }} />;
                        }
                        return candidates.map((product) => (
                          <div
                            key={product.cwrPcataloguelinesId || product.mProductId}
                            onClick={() => handleSelectResult(index, product)}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", cursor: "pointer", fontSize: 11 }}
                          >
                            <div>
                              <div style={{ fontWeight: 500, color: "#111" }}>{product.productName}</div>
                              <div style={{ fontSize: 10, color: "#6b7280" }}>
                                {formatCurrency(product.sellingPrice)} · SKU: {product.productCode}
                              </div>
                            </div>
                            <span style={{ fontSize: 10, color: product.availabilityStatus === "Yes" ? "#16A34A" : "#E99013" }}>
                              {product.sellableQty != null ? `Stock: ${product.sellableQty}` : ""}
                            </span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {replacement && !isActive && (
                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#166534", marginBottom: 4 }}>Replacement Preview</div>
                    <div style={{ color: "#DC2626", fontSize: 11, textDecoration: "line-through" }}>
                      {item.name} · {formatCurrency(item.price)}
                    </div>
                    <div style={{ textAlign: "center", fontSize: 16, color: "#16A34A", margin: "4px 0" }}>↓</div>
                    <div style={{ color: "#16A34A", fontWeight: 600, fontSize: 11 }}>
                      {replacement.productName} · {formatCurrency(replacement.sellingPrice)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 6, paddingTop: 6, borderTop: "1px dashed #BBF7D0" }}>
                      <span style={{ color: "#6b7280" }}>Price difference</span>
                      <span style={{ color: "#16A34A", fontWeight: 600 }}>
                        {formatCurrency((Number(replacement.sellingPrice) || 0) - (Number(item.price) || 0))}
                        {Number(replacement.sellingPrice) === Number(item.price) ? " (no change)" : ""}
                      </span>
                    </div>
                    <Button type="link" size="small" onClick={() => handleChangeReplacement(index)} style={{ fontSize: 10, padding: "4px 0 0" }}>
                      Change
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>Review the changes below. Once confirmed, the order will be updated immediately.</div>

          <div style={{ fontSize: 12, fontWeight: 600, color: "#111", marginBottom: 6 }}>Changes</div>
          {Object.entries(replacements).map(([index, product]) => {
            const item = items[index];
            return (
              <div key={index} style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#166534" }}>Line {item.line || Number(index) + 1} — Product Substitution</span>
                  <Tag style={{ background: "#DCFCE7", color: "#16A34A", border: "1px solid #BBF7D0", fontSize: 8 }}>MODIFIED</Tag>
                </div>
                <div style={{ color: "#DC2626", fontSize: 11, textDecoration: "line-through" }}>
                  {item.name} · Qty: {item.quantity} · {formatCurrency(item.price)}
                </div>
                <div style={{ textAlign: "center", fontSize: 16, color: "#16A34A", margin: "4px 0" }}>↓</div>
                <div style={{ color: "#16A34A", fontWeight: 600, fontSize: 11 }}>
                  {product.productName} · Qty: {item.quantity} · {formatCurrency(product.sellingPrice)}
                </div>
              </div>
            );
          })}

          <div style={{ fontSize: 12, fontWeight: 600, color: "#111", marginBottom: 6 }}>Financial Impact</div>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: "#6b7280" }}>Original Total</span>
              <span style={{ fontWeight: 500, color: "#111" }}>{formatCurrency(originalTotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: "#6b7280" }}>Amended Total</span>
              <span style={{ fontWeight: 500, color: "#111" }}>{formatCurrency(amendedTotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, borderTop: "1px solid #e5e7eb", paddingTop: 6, marginTop: 4 }}>
              <span style={{ color: "#6b7280" }}>
                <strong>Net Difference</strong>
              </span>
              <span style={{ fontWeight: 500, color: netDifference > 0 ? "#16A34A" : netDifference < 0 ? "#DC2626" : "#111" }}>
                <strong>{formatCurrency(netDifference)}</strong>
              </span>
            </div>
          </div>

          <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "8px 10px", marginTop: 12, fontSize: 11, color: "#166534" }}>
            Order records will be updated immediately. The modified items will appear on the sales invoice when the order is dispatched.
          </div>
        </>
      )}
    </Modal>
  );
};

export default ModifyItemsModal;
